import { useMemo, useState } from "react";
import {
  Button as AriaButton,
  ComboBox,
  FieldError,
  Input as AriaInput,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Text,
} from "react-aria-components";
import { ChevronDown } from "lucide-react";
import { rankSearchMatches } from "../../lib/format/normalize";
import { useModalPortalContainer } from "./Modal";
import "./SearchCombobox.css";

export interface ComboboxOption {
  id: string;
  label: string;
  /** Disambiguation text shown alongside the label (e.g. department, employee code). */
  description?: string;
  /**
   * Business code (Worker.employeeCode / Product.code / Operation.code),
   * when the entity has one — NORMAX_PERFORMANCE_BUSINESS_CODES_FAST_SEARCH_IMPLEMENTATION_REPORT.md.
   * Ranked ahead of name matches (exact, then prefix) by `rankSearchMatches`.
   * Kept structurally separate from `label`/`description` on purpose — a
   * dedicated search field, not a hidden string concatenated into the
   * visible label. Optional: every pre-existing call site that never sets
   * this keeps working unchanged, ranked purely by name.
   */
  businessCode?: string | null;
}

export interface SearchComboboxProps {
  label: string;
  items: ComboboxOption[];
  selectedKey: string | null;
  onSelectionChange: (key: string | null) => void;
  placeholder?: string;
  required?: boolean;
  error?: string | null;
  isDisabled?: boolean;
  id?: string;
  emptyMessage?: string;
  /** Ref-forwarding hook target so callers can imperatively focus after selection (Ctrl+Enter flow). */
  inputRef?: React.Ref<HTMLInputElement>;
  /**
   * Additive only — never replaces React Aria's own Enter/Arrow/Escape
   * handling (which always runs first; see the Enter guard below). Intended
   * for Daily Entry's Enter-to-next-field flow
   * (docs/NORMAX_PERFORMANCE_DAILY_ENTRY_ENTER_KEYBOARD_FLOW_FORENSIC_REPORT.md
   * §5/§13): called on every keydown React Aria itself doesn't fully own,
   * so a caller can e.g. advance focus once Enter is no longer confirming an
   * open-popover selection.
   */
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
}

/**
 * Accessible search combobox (React Aria, react-aria-components) — Proizvod/
 * Operacija/Radnik pickers (DAILY_ENTRY_INTERACTION_SPEC.md §5,
 * FRONTEND_ARCHITECTURE_AND_DESIGN_SYSTEM.md §1). Fuzzy, case- and
 * diacritic-insensitive substring filter; full keyboard support (typing,
 * ArrowUp/Down, Enter to select, Escape to close without selecting) comes
 * from react-aria-components' own WAI-ARIA-compliant implementation.
 */
export function SearchCombobox({
  label,
  items,
  selectedKey,
  onSelectionChange,
  placeholder,
  required,
  error,
  isDisabled,
  id,
  emptyMessage = "Nema rezultata.",
  inputRef,
  onKeyDown,
}: SearchComboboxProps) {
  const [filterText, setFilterText] = useState("");
  const portalContainer = useModalPortalContainer();

  // `inputValue` is deliberately left UNCONTROLLED — React Aria's ComboBox
  // manages its own displayed text internally (typing, and syncing the text
  // to the selected item's label on selection, both work natively). Only
  // `onInputChange` is observed, purely to drive the custom fuzzy filter
  // below; feeding it back as a controlled `inputValue` was tried first, but
  // with `menuTrigger="input"` React Aria treats ANY change to a *controlled*
  // inputValue — including the programmatic one needed to sync the display to
  // a fresh selection — as a fresh "input change," which reopens the popover
  // right after selection closed it. That reopen, happening one render after
  // Radix's Dialog already finished its own "what's the current top layer"
  // bookkeeping, left the rest of the modal's content marked `aria-hidden`
  // behind the unexpectedly-reopened popover.
  const filteredItems = useMemo(() => rankSearchMatches(items, filterText), [items, filterText]);

  return (
    <ComboBox
      id={id}
      className="nx-combobox"
      items={filteredItems}
      selectedKey={selectedKey}
      onSelectionChange={(key) => onSelectionChange(key as string | null)}
      onInputChange={setFilterText}
      isDisabled={isDisabled}
      isInvalid={!!error}
      allowsEmptyCollection
      menuTrigger="input"
    >
      <Label className="nx-field__label">
        {label}
        {required ? <span className="nx-field__required">*</span> : null}
      </Label>
      <div className="nx-combobox__control">
        <AriaInput
          ref={inputRef}
          className="nx-combobox__input"
          placeholder={placeholder ?? "Pretraži..."}
          onKeyDown={(e) => {
            // React Aria's own ComboBox keyboard handling (Enter selects the
            // highlighted option and closes the popover, via `state.commit()`)
            // always runs before this — it's composed into the SAME input's
            // keydown chain, ahead of this consumer-supplied handler. That
            // internal handling calls React state setters, which React defers
            // committing to the DOM until after this synchronous handler chain
            // returns; so `aria-expanded` here still reflects the PRE-commit
            // value for this exact keystroke. That's exactly the signal this
            // needs: if it reads "true", this Enter just confirmed a
            // selection (or is otherwise interacting with an open popover —
            // ArrowUp/Down, typing) and must not ALSO be treated as a
            // next-field advance in the same keystroke; the option is
            // selected, the popover closes, and only the NEXT physical Enter
            // (a separate keydown, by which point React has re-rendered and
            // aria-expanded correctly reads "false") reaches the caller.
            // Verified in a real browser, not assumed from source alone — see
            // docs/NORMAX_PERFORMANCE_DAILY_ENTRY_ENTER_KEYBOARD_FLOW_FORENSIC_REPORT.md
            // §5/§13.
            if (e.key === "Enter" && e.currentTarget.getAttribute("aria-expanded") === "true") return;
            onKeyDown?.(e);
          }}
        />
        <AriaButton className="nx-combobox__trigger" aria-label="Prikaži listu">
          <ChevronDown size={14} />
        </AriaButton>
      </div>
      {error ? (
        <FieldError className="nx-field__error">{error}</FieldError>
      ) : null}
      <Popover className="nx-combobox__popover" UNSTABLE_portalContainer={portalContainer}>
        <ListBox className="nx-combobox__listbox" renderEmptyState={() => (
          <div className="nx-combobox__empty">{emptyMessage}</div>
        )}>
          {(item: ComboboxOption) => (
            <ListBoxItem id={item.id} textValue={item.label} className="nx-combobox__item">
              <span>{item.label}</span>
              {item.description ? <Text slot="description">{item.description}</Text> : null}
            </ListBoxItem>
          )}
        </ListBox>
      </Popover>
    </ComboBox>
  );
}
